import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, rolesOf } from "../lib/roles";
import { Field, PageHeader, Panel } from "../components/common";
import { InstallApp } from "../components/InstallApp";
import { WakeSetting } from "../components/WakeSetting";
import { AvatarEditor } from "../components/AvatarEditor";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-3 border-b last:border-0 text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-right">{value || "—"}</span></div>
);

function EditDialog({ open, onClose, user, refresh }) {
  const [f, setF] = useState({ indirizzo: user.indirizzo || "", telefono: user.telefono || "", email: user.email || "" });
  const save = async () => {
    if (!f.email.trim()) return toast.error("L'email non può essere vuota");
    try { await api("/auth/update-profile", { method: "POST", body: { ...f, email: f.email.trim() } }); await refresh(); toast.success("Dati aggiornati"); onClose(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Modifica i tuoi dati</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <Field label="Indirizzo di residenza"><Input value={f.indirizzo} onChange={(e) => setF({ ...f, indirizzo: e.target.value })} data-testid="profile-address-input" /></Field>
        <Field label="Telefono"><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} placeholder="+39 ..." data-testid="profile-phone-input" /></Field>
        <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="profile-email-input" /></Field>
        <Button onClick={save} className="w-full rounded-full bg-[var(--teal)]" data-testid="profile-save-button">Salva modifiche</Button>
      </div></DialogContent></Dialog>
  );
}

function PasswordDialog({ open, onClose }) {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const save = async () => {
    if (!f.current || !f.next) return toast.error("Compila tutti i campi");
    if (f.next.length < 6) return toast.error("La nuova password deve avere almeno 6 caratteri");
    if (f.next !== f.confirm) return toast.error("Le password non coincidono");
    try { await api("/auth/change-password", { method: "POST", body: { current_password: f.current, new_password: f.next } }); toast.success("Password aggiornata"); onClose(); } catch (e) { toast.error(e.message); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Cambia password</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <Field label="Password attuale"><Input type="password" value={f.current} onChange={set("current")} data-testid="pwd-current-input" /></Field>
        <Field label="Nuova password"><Input type="password" value={f.next} onChange={set("next")} placeholder="min. 6 caratteri" data-testid="pwd-new-input" /></Field>
        <Field label="Conferma nuova password"><Input type="password" value={f.confirm} onChange={set("confirm")} data-testid="pwd-confirm-input" /></Field>
        <Button onClick={save} className="w-full rounded-full bg-[var(--teal)]" data-testid="pwd-save-button">Aggiorna password</Button>
      </div></DialogContent></Dialog>
  );
}

export default function Profilo() {
  const { user, signOut, refresh } = useAuth();
  const nav = useNavigate();
  const [dlg, setDlg] = useState(null);
  return (
    <div>
      <PageHeader subtitle="Profilo" title={`${user.nome} ${user.cognome}`} />
      <div className="mb-6"><AvatarEditor uid={user.id} self /></div>
      <div className="flex flex-wrap gap-2 mb-8">{rolesOf(user).map((r) => <span key={r} className="tag tag-teal">{r}</span>)}{user.legge_104 && <span className="tag tag-amber">Legge 104</span>}</div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel data-testid="profile-data"><h2 className="font-display font-bold text-lg text-[var(--navy)] mb-2">Dati anagrafici</h2>
          <Row label="Email" value={user.email} /><Row label="Telefono" value={user.telefono} /><Row label="Data di nascita" value={user.data_nascita} />
          <Row label="Luogo di nascita" value={user.luogo_nascita} /><Row label="Codice Fiscale" value={user.codice_fiscale} /><Row label="Indirizzo" value={[user.indirizzo, user.citta].filter(Boolean).join(", ")} />
        </Panel>
        <div className="space-y-6">
          <Panel><h2 className="font-display font-bold text-lg text-[var(--navy)] mb-2">Lavoro</h2>
            <Row label="Visita medica del lavoro" value={user.data_visita_medica ? fmtDate(user.data_visita_medica) : ""} /><Row label="Ore settimanali" value={user.ore_settimanali} /><Row label="Giorni settimanali" value={user.giorni_settimanali} />
          </Panel>
          <WakeSetting />
          <InstallApp />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setDlg("edit")} data-testid="edit-profile-button"><Pencil className="w-4 h-4 mr-1" />Modifica i tuoi dati</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setDlg("pwd")} data-testid="change-password-button"><KeyRound className="w-4 h-4 mr-1" />Cambia password</Button>
            <Button variant="ghost" className="rounded-full text-[var(--red)]" onClick={() => { signOut(); nav("/login"); }} data-testid="profile-logout-button"><LogOut className="w-4 h-4 mr-1" />Esci</Button>
          </div>
        </div>
      </div>
      {dlg === "edit" && <EditDialog open onClose={() => setDlg(null)} user={user} refresh={refresh} />}
      {dlg === "pwd" && <PasswordDialog open onClose={() => setDlg(null)} />}
    </div>
  );
}
